/**
 * ImportReviewBar — docked row above compose for the import review sheet
 * (spec §4.5). `active` shows the row counts plus a flagged badge and opens
 * the sheet on tap; `saved`/`undone` are receipts rendered from the transcript
 * replay when the preview itself is already null. The ✕ dismisses the bar (the
 * transcript stays the store — a new snapshot re-arms it), mirroring
 * ThesisDraftBar.
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { flaggedCount } from '../../services/importPreview';
import type { ImportPreview } from '../../services/importPreview';

/** The replay lifecycle the bar renders; `none`/`discarded` render nothing. */
export type ImportReviewBarState = 'active' | 'saved' | 'undone' | 'none' | 'discarded';

export function ImportReviewBar({
  state,
  preview,
  onOpen,
  onDismiss,
  onUndo,
  undoable,
}: {
  state: ImportReviewBarState;
  preview: ImportPreview | null;
  onOpen: () => void;
  onDismiss: () => void;
  onUndo?: () => void;
  undoable?: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // State-first guard: replay returns {preview: null, state: 'saved'|'undone'}
  // for receipt-terminal transcripts, so the receipts render WITHOUT a
  // preview; only active needs rows.
  if (state === 'none' || state === 'discarded') return null;
  if (state === 'active' && (preview === null || preview.rows.length === 0)) return null;

  const positions = preview?.rows.filter((r) => r.kind === 'position').length ?? 0;
  const trades = preview?.rows.filter((r) => r.kind === 'trade').length ?? 0;
  const flagged = preview ? flaggedCount(preview) : 0;
  const barLabel = t('chat.import.bar', {
    defaultValue: 'Import · {{p}} positions · {{t}} trades',
    p: positions,
    t: trades,
  });

  return (
    <View
      testID="import-review-bar"
      style={[
        styles.row,
        { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
      ]}
    >
      {state === 'active' ? (
        <Pressable
          testID="import-bar-open"
          onPress={onOpen}
          accessibilityRole="button"
          accessibilityLabel={barLabel}
          style={styles.main}
        >
          <Text style={[styles.label, { color: theme.colors.primary }]}>▤ {barLabel}</Text>
          {flagged > 0 ? (
            <Pressable
              testID="import-bar-flagged"
              onPress={() => {}} // swallows the tap: the badge never opens the sheet
              accessibilityRole="text"
              accessibilityLabel={t('chat.import.flagged', {
                defaultValue: '{{n}} flagged',
                n: flagged,
              })}
              style={[
                styles.badge,
                {
                  backgroundColor: theme.colors.warningBackground,
                  borderColor: theme.colors.warningBorder,
                },
              ]}
            >
              <Text style={[styles.badgeText, { color: theme.colors.warningText }]}>
                ◔ {t('chat.import.flagged', { defaultValue: '{{n}} flagged', n: flagged })}
              </Text>
            </Pressable>
          ) : null}
        </Pressable>
      ) : (
        <View style={styles.main}>
          {state === 'saved' ? (
            <>
              <Text style={[styles.label, { color: theme.colors.success }]}>
                ✓ {t('chat.import.saved', { defaultValue: 'imported' })}
              </Text>
              {undoable !== false && onUndo ? (
                <Pressable
                  testID="import-bar-undo"
                  onPress={onUndo}
                  accessibilityRole="button"
                  accessibilityLabel={t('actions.undo', { defaultValue: 'Undo' })}
                  style={styles.undo}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={[styles.undoText, { color: theme.colors.primary }]}>
                    [{t('actions.undo', { defaultValue: 'Undo' })}]
                  </Text>
                </Pressable>
              ) : null}
            </>
          ) : (
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              ↩ {t('chat.import.undone', { defaultValue: 'import undone' })}
            </Text>
          )}
        </View>
      )}
      <Pressable
        testID="import-bar-dismiss"
        onPress={onDismiss}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
        style={styles.dismiss}
      >
        <Text style={[styles.dismissText, { color: theme.colors.textTertiary }]}>✕</Text>
      </Pressable>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      borderTopWidth: 1,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    main: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
      flexShrink: 1,
    },
    badge: {
      borderWidth: 1,
      borderRadius: theme.radii.sm,
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    badgeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
    },
    undo: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    undoText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    dismiss: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
